import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isTourV2Item,
  isBedbankItem,
} from 'lib/checkout/checkoutUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { sum } from 'lib/array/arrayUtils'
import getCurrentPaymentSchedule from './getCurrentPaymentSchedule'

export const getPaymentScheduleTemplateIdOfCartItem = createSelector(
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.checkout.cart.items,
  (tourV2Offers, bedbankOfferRates, items): string | undefined | null => {
    const tourV2Items = items.filter(isTourV2Item)
    const bedbankItems = items.filter(isBedbankItem)

    // First check tour items
    for (const item of tourV2Items) {
      const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
      if (offer?.paymentScheduleInfo?.templateId) {
        return offer.paymentScheduleInfo.templateId
      }
    }

    // If no tour item with template ID found, check bedbank items
    for (const item of bedbankItems) {
      const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
      const bedbankOfferRatesForItem = bedbankOfferRates[item.offerId]?.[rateKey]
      const bedbankRate = bedbankOfferRatesForItem?.find(rate => rate.id === item.roomRateId)
      if (bedbankRate?.paymentScheduleInfo?.templateId) {
        return bedbankRate.paymentScheduleInfo.templateId
      }
    }

    return undefined
  },
)

export const getServiceFeeForInitialPayment = createSelector(
  getCurrentPaymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule?.eligible || !paymentSchedule?.charges) {
      return 0
    }
    return paymentSchedule.charges[0]?.feeAmount || 0
  },
)

export const getPaymentScheduleTotalServiceFee = createSelector(
  getCurrentPaymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule?.eligible || !paymentSchedule?.charges) {
      return 0
    }
    return sum(paymentSchedule.charges, (schedule) => schedule.feeAmount)
  },
)

export const getServiceFeeForLaterPayments = createSelector(
  getPaymentScheduleTotalServiceFee,
  getServiceFeeForInitialPayment,
  (totalServiceFee, initialServiceFee): number => {
    return totalServiceFee - initialServiceFee
  },
)

export const getServiceFeePaymentScheduleData = createSelector(
  getCurrentPaymentSchedule,
  (paymentSchedule): App.PaymentScheduleServiceFeeData | undefined => {
    const chargeIndex = paymentSchedule?.charges.findIndex(schedule => schedule.feeAmount > 0) ?? -1

    if (chargeIndex === -1 || !paymentSchedule?.charges?.[chargeIndex]) {
      return undefined
    }

    return {
      chargeIndex,
      feeAmount: paymentSchedule.charges[chargeIndex].feeAmount,
      paymentType: paymentSchedule.charges[chargeIndex].paymentType,
    }
  },
)
